import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import BirthdayPicker from "@/components/BirthdayPicker";

export default function Account() {
  const { user, logout, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", postal_code: "", country: "US" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [birthdayCoupon, setBirthdayCoupon] = useState(null);
  const [prefs, setPrefs] = useState({
    favorite_player: "", favorite_team: "", favorite_card_type: "",
    favorite_sport: "", preferred_brand: "", birthday: "",
    email_opt_in: false, email_cadence: "",
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [sports, setSports] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (user) {
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
      if (user.shipping_address) setAddr({ ...addr, ...user.shipping_address });
      setPrefs({
        favorite_player: user.favorite_player || "",
        favorite_team: user.favorite_team || "",
        favorite_card_type: user.favorite_card_type || "",
        favorite_sport: user.favorite_sport || "",
        preferred_brand: user.preferred_brand || "",
        birthday: user.birthday || "",
        email_opt_in: !!user.email_opt_in,
        email_cadence: user.email_cadence || "",
      });
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    api.get("/filters?kind=sport").then((r) => setSports(r.data)).catch(() => {});
    api.get("/filters?kind=brand").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/auth/me/birthday-coupon")
      .then((r) => setBirthdayCoupon(r.data && r.data.code ? r.data : null))
      .catch(() => {});
  }, [user]);

  const save = async () => {
    try { await api.put("/auth/me/address", addr); await refresh(); toast.success("Address saved"); }
    catch { toast.error("Failed to save"); }
  };

  const savePrefs = async () => {
    setPrefsSaving(true);
    try {
      await api.put("/auth/me/profile", {
        favorite_player: prefs.favorite_player || null,
        favorite_team: prefs.favorite_team || null,
        favorite_card_type: prefs.favorite_card_type || null,
        favorite_sport: prefs.favorite_sport || null,
        preferred_brand: prefs.preferred_brand || null,
        birthday: prefs.birthday || null,
        email_opt_in: !!prefs.email_opt_in,
        email_cadence: prefs.email_opt_in ? (prefs.email_cadence || null) : null,
      });
      await refresh();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save preferences");
    } finally {
      setPrefsSaving(false);
    }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setPwSaving(true);
    try {
      await api.post("/auth/me/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update password");
    } finally {
      setPwSaving(false);
    }
  };

  if (!user) return null;

  const tierName = { rookie: "Rookie", pro: "Pro", hof: "Hall of Fame" }[user.subscription_tier];

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-label text-zinc-500">Member</div>
          <h1 className="font-display text-5xl mt-2">Hi, {user.name}</h1>
          <div className="text-zinc-400 mt-1">{user.email}</div>
        </div>
        <button onClick={logout} className="btn-outline text-sm" data-testid="logout-btn">Log out</button>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-12">
        <Card title="Membership" testid="membership-card">
          {tierName ? (
            <>
              <div className="font-display text-3xl" style={{ color: "var(--cl-orange)" }}>{tierName}</div>
              <p className="text-sm text-zinc-400 mt-2">Active subscription. Tier discounts apply automatically at checkout.</p>
            </>
          ) : (
            <>
              <div className="font-display text-3xl text-zinc-400">No plan</div>
              <a href="/subscribe" className="btn-primary text-sm mt-4 inline-block">Choose a Tier</a>
            </>
          )}
        </Card>

        <Card title="Shipping Address" className="md:col-span-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <FieldSmall label="Address line 1" value={addr.line1} onChange={(v) => setAddr({ ...addr, line1: v })} testid="addr-line1" full />
            <FieldSmall label="City" value={addr.city} onChange={(v) => setAddr({ ...addr, city: v })} testid="addr-city" />
            <FieldSmall label="State" value={addr.state} onChange={(v) => setAddr({ ...addr, state: v })} testid="addr-state" />
            <FieldSmall label="Postal Code" value={addr.postal_code} onChange={(v) => setAddr({ ...addr, postal_code: v })} testid="addr-zip" />
            <FieldSmall label="Country" value={addr.country} onChange={(v) => setAddr({ ...addr, country: v })} testid="addr-country" />
          </div>
          <button onClick={save} className="btn-primary text-sm mt-4" data-testid="save-address">Save Address</button>
        </Card>
      </div>

      {birthdayCoupon && (
        <div className="mt-8 border border-[var(--cl-orange)]/40 rounded p-6 bg-gradient-to-br from-[#1a0505] to-[var(--cl-surface)] flex flex-wrap items-center justify-between gap-4"
             data-testid="birthday-coupon-card">
          <div>
            <div className="text-label text-[var(--cl-orange)]">🎂 Birthday Perk</div>
            <div className="font-display text-2xl mt-1">{birthdayCoupon.percent_off}% off your next order</div>
            <div className="text-sm text-zinc-400 mt-1">
              Expires {birthdayCoupon.expires_at?.slice(0, 10)} · one-time use
            </div>
          </div>
          <div className="flex items-center gap-3">
            <code className="bg-black border border-[var(--cl-orange)]/40 rounded px-3 py-2 font-mono text-[var(--cl-orange)]" data-testid="birthday-coupon-code">
              {birthdayCoupon.code}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(birthdayCoupon.code); toast.success("Copied — paste at checkout"); }}
              className="btn-outline text-xs"
              data-testid="birthday-coupon-copy">
              Copy
            </button>
          </div>
        </div>
      )}

      <div className="mt-12" data-testid="preferences-card">
        <h2 className="font-display text-3xl">My Collecting Preferences</h2>
        <p className="text-sm text-zinc-400 mt-1">Help us tailor drops, breaks, and newsletter deals to what you actually collect.</p>
        <div className="mt-4 border border-white/10 rounded p-6 bg-[var(--cl-surface)] grid md:grid-cols-2 gap-4">
          <FieldSmall label="Favorite Player" value={prefs.favorite_player}
                      onChange={(v) => setPrefs({ ...prefs, favorite_player: v })} testid="pref-player" />
          <FieldSmall label="Favorite Team" value={prefs.favorite_team}
                      onChange={(v) => setPrefs({ ...prefs, favorite_team: v })} testid="pref-team" />
          <FieldSmall label="Favorite Card Type (e.g. Autographs, Rookies)"
                      value={prefs.favorite_card_type}
                      onChange={(v) => setPrefs({ ...prefs, favorite_card_type: v })} testid="pref-type" full />
          <SelectSmall label="Favorite Sport" value={prefs.favorite_sport}
                       options={[["", "—"], ...sports.map((s) => [s.key, s.label])]}
                       onChange={(v) => setPrefs({ ...prefs, favorite_sport: v })} testid="pref-sport" />
          <SelectSmall label="Preferred Brand" value={prefs.preferred_brand}
                       options={[["", "—"], ...brands.map((b) => [b.key, b.label])]}
                       onChange={(v) => setPrefs({ ...prefs, preferred_brand: v })} testid="pref-brand" />

          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              <span>Birthday</span>
              <span className="text-[10px] text-zinc-600 normal-case tracking-normal">optional · unlock birthday perks</span>
            </label>
            <div className="mt-1">
              <BirthdayPicker value={prefs.birthday}
                              onChange={(v) => setPrefs({ ...prefs, birthday: v })}
                              testidPrefix="pref-birthday" />
            </div>
          </div>

          <div className="md:col-span-2 pt-4 mt-2 border-t border-white/10">
            <label className="flex items-center gap-3 text-sm">
              <input type="checkbox" checked={prefs.email_opt_in}
                     onChange={(e) => setPrefs({ ...prefs, email_opt_in: e.target.checked })}
                     data-testid="pref-opt-in" />
              <span>Yes, send me the Card Lobster newsletter with drops and deals</span>
            </label>
            {prefs.email_opt_in && (
              <div className="mt-3">
                <SelectSmall label="How often?" value={prefs.email_cadence}
                             options={[["", "— pick one —"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]]}
                             onChange={(v) => setPrefs({ ...prefs, email_cadence: v })} testid="pref-cadence" />
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <button onClick={savePrefs} disabled={prefsSaving} className="btn-primary text-sm" data-testid="save-prefs">
              {prefsSaving ? "Saving…" : "Save Preferences"}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-display text-3xl">Change Password</h2>
        <div className="mt-4 border border-white/10 rounded p-6 bg-[var(--cl-surface)] max-w-xl" data-testid="change-password-card">
          <div className="grid gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Current password</label>
              <input type="password" value={pw.current_password}
                     onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                     data-testid="pw-current"
                     className="w-full mt-1 bg-black border border-white/10 rounded px-3 py-2 focus:outline-none focus:border-[var(--cl-primary)]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">New password (min 8 chars)</label>
              <input type="password" value={pw.new_password}
                     onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                     data-testid="pw-new"
                     className="w-full mt-1 bg-black border border-white/10 rounded px-3 py-2 focus:outline-none focus:border-[var(--cl-primary)]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">Confirm new password</label>
              <input type="password" value={pw.confirm}
                     onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                     data-testid="pw-confirm"
                     className="w-full mt-1 bg-black border border-white/10 rounded px-3 py-2 focus:outline-none focus:border-[var(--cl-primary)]" />
            </div>
          </div>
          <button onClick={changePassword} disabled={pwSaving} className="btn-primary text-sm mt-4" data-testid="change-password-btn">
            {pwSaving ? "Saving…" : "Update Password"}
          </button>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-display text-3xl">Order History</h2>
        {orders.length === 0 ? (
          <div className="mt-4 text-zinc-500 text-sm">No orders yet.</div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="orders-list">
            {orders.map((o) => (
              <div key={o.id} className="border border-white/10 rounded p-4 bg-[var(--cl-surface)] flex justify-between items-center flex-wrap gap-3">
                <div>
                  <div className="text-label text-zinc-500">Order · {o.created_at.slice(0, 10)}</div>
                  <div className="text-sm mt-1">{o.items.length} item(s) · {o.shipping_status}</div>
                  {o.tracking && <TrackingLink carrier={o.carrier} tracking={o.tracking} orderId={o.id} />}
                </div>
                <div className="font-display text-2xl">${o.total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`border border-white/10 rounded p-6 bg-[var(--cl-surface)] ${className}`}>
      <div className="text-label text-zinc-500">{title}</div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const CARRIER_LABEL = { usps: "USPS", ups: "UPS", fedex: "FedEx", dhl: "DHL", other: "Carrier" };
const trackingUrl = (carrier, n) => {
  if (!n) return null;
  const num = n.trim();
  switch ((carrier || "").toLowerCase()) {
    case "usps": return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${num}`;
    case "ups": return `https://www.ups.com/track?tracknum=${num}`;
    case "fedex": return `https://www.fedex.com/fedextrack/?trknbr=${num}`;
    case "dhl": return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?tracking-id=${num}`;
    default: return null;
  }
};

function TrackingLink({ carrier, tracking, orderId }) {
  const url = trackingUrl(carrier, tracking);
  const label = CARRIER_LABEL[(carrier || "").toLowerCase()] || "Carrier";
  return (
    <div className="mt-2 text-xs text-zinc-400">
      {label} #{tracking}
      {url && (
        <a href={url} target="_blank" rel="noreferrer"
           data-testid={`track-package-${orderId}`}
           className="ml-3 inline-flex items-center gap-1 text-[var(--cl-orange)] font-semibold hover:underline">
          Track Package →
        </a>
      )}
    </div>
  );
}

function FieldSmall({ label, value, onChange, testid, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="w-full mt-1 bg-black border border-white/10 rounded px-3 py-2 focus:outline-none focus:border-[var(--cl-primary)]" />
    </div>
  );
}

function SelectSmall({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full mt-1 bg-black border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--cl-primary)]">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
